// bluaIQ — calendar dates without a time (PR3, C5).
//
// Bug: a date with no time ("2026-03-16" from an <input type="date">, a quote
// date, a receipt date, a due date) was parsed as MIDNIGHT UTC and then shown
// in the viewer's zone. In Dallas (UTC-5/-6) midnight UTC is the evening of
// the PREVIOUS day, so every such date showed one day early (quote 03/16 →
// 15 mar; receipt 5/11 → 10 may).
//
// Rule: a calendar date is a DAY, not an instant.
//   * Write it at NOON UTC (`toDateOnlyIso`) — the same calendar day in every
//     US time zone, so even old code that renders it in local time is right.
//   * Read it by its UTC calendar day (`dateOnlyKey` / `formatDateOnly`) — this
//     also fixes rows already stored at midnight UTC.
// Never use these helpers for real instants (createdAt, signedAt, sentAt).
//
// Pure module — safe in the browser, the server and tests.

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/** "2026-03-16" → "2026-03-16T12:00:00.000Z". Other values pass through as ISO, or null. */
export function toDateOnlyIso(value: string | Date | null | undefined): string | null {
  if (value == null || value === '') return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    const m = DATE_ONLY.exec(trimmed)
    if (m) {
      const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0))
      // reject impossible dates like 2026-02-31 (Date.UTC rolls them over)
      if (d.getUTCFullYear() !== Number(m[1]) || d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) return null
      return d.toISOString()
    }
    const parsed = new Date(trimmed)
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
  }
  return Number.isNaN(value.getTime()) ? null : value.toISOString()
}

/** The calendar day a stored date-only value means: "YYYY-MM-DD" (UTC day), or null. */
export function dateOnlyKey(value: string | Date | null | undefined): string | null {
  if (value == null || value === '') return null
  if (typeof value === 'string' && DATE_ONLY.test(value.trim())) return value.trim()
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString().slice(0, 10)
}

/** Format a date-only value by its calendar day, independent of the viewer's zone. */
export function formatDateOnly(
  value: string | Date | null | undefined,
  locale: string = 'en-US',
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
  empty = '—',
): string {
  const key = dateOnlyKey(value)
  if (!key) return empty
  const [y, m, d] = key.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(locale, { ...options, timeZone: 'UTC' })
}

/** Value for an <input type="date"> from a stored date-only value. */
export function toDateInputValue(value: string | Date | null | undefined): string {
  return dateOnlyKey(value) ?? ''
}

// Midnight with a ZERO UTC offset in any ISO spelling — Z, +00:00, -00:00, +0000, +00
// (Codex PR3 R4 [P1]: "+00:00" was read as a real instant and slid to the previous
// day in Dallas). A NON-zero offset (e.g. 18:00-06:00) is a real local moment and
// is deliberately NOT matched, even though it is the same instant as 00:00Z.
const MIDNIGHT_UTC_ISO = /^(\d{4})-(\d{2})-(\d{2})[Tt ]00:00(?::00(?:\.0+)?)?(?:[Zz]|[+-]00(?::?00)?)$/

/**
 * Codex PR3 [P1] — WRITE side for every calendar-day column (quote date, receipt
 * date, contract start, draw / payment due date, paid date, task and policy
 * dates). Accepts what the clients actually send:
 *   * "2026-03-16"                → 2026-03-16T12:00:00.000Z
 *   * "2026-03-16T00:00:00.000Z"  → 2026-03-16T12:00:00.000Z (a <input type="date">
 *     value that went through `new Date(v).toISOString()` — midnight UTC IS the
 *     date-only shape; keeping it would show the previous day in Texas)
 *   * any other real instant      → unchanged
 * undefined stays undefined (field not sent → not touched); null/'' → null.
 */
export function toCalendarDay(value: string | Date | null | undefined): Date | null | undefined {
  if (value === undefined) return undefined
  if (value === null || value === '') return null
  if (typeof value === 'string') {
    const m = MIDNIGHT_UTC_ISO.exec(value.trim())
    const iso = toDateOnlyIso(m ? `${m[1]}-${m[2]}-${m[3]}` : value)
    return iso ? new Date(iso) : null
  }
  if (Number.isNaN(value.getTime())) return null
  return toCalendarDay(value.toISOString())
}
